import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from './components/AdminLayout';
import { CustomerPicker } from './components/CustomerPicker';
import { CustomerModal } from './components/CustomerModal';
import { API_BASE_URL } from '../../config/apiConfig';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`;

const PageTitle = styled.h1`
  margin: 0;
  color: #2c3e50;
  font-size: 28px;
`;

const BackButton = styled.button`
  background: none;
  border: 1px solid #ddd;
  padding: 8px 16px;
  border-radius: 6px;
  color: #495057;
  cursor: pointer;
  font-size: 14px;
  
  &:hover {
    background: #f8f9fa;
  }
`;

const FormContainer = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const MainColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SideColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Card = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: visible;
  
  &.overflow-hidden {
    overflow: hidden;
  }
`;

const CardHeader = styled.div`
  padding: 16px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  color: #2c3e50;
  font-weight: 600;
`;

const CardBody = styled.div`
  padding: 20px;
  position: relative;
  
  &.customer-section {
    min-height: 100px;
    overflow: visible;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  
  &.full-width {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #495057;
  
  span {
    color: #e74c3c;
  }
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid ${props => props.error ? '#e74c3c' : '#ddd'};
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: ${props => props.error ? '#e74c3c' : '#3498db'};
  }
  
  &:disabled {
    background: #f8f9fa;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const Textarea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
  
  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const Checkbox = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #495057;
  cursor: pointer;
  
  input {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
`;

const ItemsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  th, td {
    padding: 10px 8px;
    text-align: left;
    border-bottom: 1px solid #e9ecef;
  }
  
  th {
    font-size: 12px;
    font-weight: 600;
    color: #6c757d;
    text-transform: uppercase;
  }
  
  td {
    vertical-align: top;
    padding-top: 12px;
  }
`;

const ItemInput = styled.input`
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
  
  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const ItemTextarea = styled.textarea`
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 12px;
  resize: vertical;
  min-height: 40px;
  max-height: 80px;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #3498db;
  }
  
  &::placeholder {
    color: #adb5bd;
  }
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #e74c3c;
  cursor: pointer;
  font-size: 18px;
  padding: 4px 8px;
  
  &:hover {
    color: #c0392b;
  }
`;

const AddItemButton = styled.button`
  background: #f0f9e7;
  border: 1px dashed #2d5a0b;
  color: #2d5a0b;
  padding: 10px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  width: 100%;
  margin-top: 12px;
  
  &:hover {
    background: #e4f5d4;
  }
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 14px;
  color: #495057;
  
  &.total {
    font-weight: 600;
    font-size: 16px;
    color: #2c3e50;
    border-top: 2px solid #e9ecef;
    padding-top: 12px;
    margin-top: 4px;
  }
`;

const TaxInfo = styled.div`
  background: #f8f9fa;
  border-radius: 6px;
  padding: 12px;
  margin-top: 16px;
  font-size: 13px;
  color: #6c757d;
`;

const TaxBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  margin-left: 8px;
  
  &.intra {
    background: #d4edda;
    color: #155724;
  }
  
  &.inter {
    background: #e7f3ff;
    color: #0066cc;
  }
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SubmitButton = styled(Button)`
  background: #e9fe53;
  border: none;
  color: #202023;
  width: 100%;
  
  &:hover:not(:disabled) {
    background: #d4e94a;
  }
`;

const SecondaryButton = styled(Button)`
  background: white;
  border: 1px solid #ddd;
  color: #495057;
  width: 100%;
  margin-top: 12px;
  
  &:hover:not(:disabled) {
    background: #f8f9fa;
  }
`;

const ErrorText = styled.div`
  color: #e74c3c;
  font-size: 13px;
  margin-top: 8px;
`;

const SuccessContainer = styled.div`
  text-align: center;
  padding: 40px 20px;
`;

const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  background: #d4edda;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 40px;
`;

const SuccessTitle = styled.h2`
  color: #155724;
  margin: 0 0 12px;
`;

const SuccessText = styled.p`
  color: #6c757d;
  margin: 0 0 24px;
`;

const InvoiceNumber = styled.div`
  font-family: monospace;
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  background: #f8f9fa;
  padding: 12px 20px;
  border-radius: 6px;
  display: inline-block;
  margin-bottom: 24px;
`;

const PincodeStatus = styled.div`
  font-size: 12px;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &.valid {
    color: #27ae60;
  }
  
  &.invalid {
    color: #e74c3c;
  }
  
  &.loading {
    color: #7f8c8d;
  }
`;

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
];

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'upi', label: 'UPI' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'card', label: 'Card' },
  { value: 'other', label: 'Other' }
];

export function InvoiceCreate() {
  const navigate = useNavigate();
  
  // Customer state
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  
  // Address state
  const [useCustomerAddress, setUseCustomerAddress] = useState(true);
  const [billingAddress, setBillingAddress] = useState({
    address: '',
    city: '',
    state: '',
    pincode: '',
    companyName: '',
    billToCompany: false,
    gst: ''
  });
  const [sameAsShipping, setSameAsShipping] = useState(true);
  const [shippingAddress, setShippingAddress] = useState({
    address: '',
    city: '',
    state: '',
    pincode: ''
  });
  
  // Pincode validation
  const [billingPincodeStatus, setBillingPincodeStatus] = useState({ loading: false, valid: null, message: '', taxType: '' });
  
  // Items state
  const [items, setItems] = useState([
    { name: '', description: '', hsn: '', quantity: 1, unitPrice: 0, gstRate: 0 }
  ]);
  // Item rate type
  const [rateIncludesGst, setRateIncludesGst] = useState(false);
  
  // Payment state
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [transactionId, setTransactionId] = useState('');
  const [notes, setNotes] = useState('');
  // Initialize with current date and time in local format
  const [invoiceDate, setInvoiceDate] = useState(() => {
    const now = new Date();
    const offset = now.getTimezoneOffset();
    const local = new Date(now.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16); // Format: YYYY-MM-DDTHH:mm
  });
  
  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Prefill company name and GST when customer changes
  useEffect(() => {
    if (selectedCustomer) {
      setBillingAddress(prev => ({
        ...prev,
        companyName: selectedCustomer.companyName || '',
        gst: selectedCustomer.gst || '',
        billToCompany: prev.billToCompany && selectedCustomer.companyName ? true : false
      }));
    }
  }, [selectedCustomer]);

  // Sync address fields from customer when using customer address
  useEffect(() => {
    if (selectedCustomer && useCustomerAddress) {
      setBillingAddress(prev => ({
        ...prev,
        address: selectedCustomer.address || '',
        city: '',
        state: '',
        pincode: selectedCustomer.pincode?.toString() || ''
      }));
    }
  }, [selectedCustomer, useCustomerAddress]);

  // Validate billing pincode with Bluedart API
  useEffect(() => {
    if (billingAddress.pincode.length !== 6) {
      setBillingPincodeStatus({ loading: false, valid: null, message: '', taxType: '', serviceable: null });
      return;
    }

    const validatePincode = async () => {
      setBillingPincodeStatus({ loading: true, valid: null, message: 'Validating with Bluedart...', taxType: '', serviceable: null });
      
      try {
        const token = sessionStorage.getItem('admin-token');
        const response = await fetch(`${API_BASE_URL}/admin/pincode/${billingAddress.pincode}`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token
          }
        });
        const result = await response.json();
        
        if (result.is_success && result.data.isValid) {
          const state = result.data.stateName || result.data.state;
          const city = result.data.city;
          
          setBillingPincodeStatus({
            loading: false,
            valid: true,
            message: `${state}${city ? `, ${city}` : ''}`,
            taxType: result.data.taxType,
            serviceable: result.data.serviceable
          });
          setBillingAddress(prev => ({
            ...prev,
            state: state || prev.state,
            city: city || prev.city
          }));
        } else {
          setBillingPincodeStatus({
            loading: false,
            valid: false,
            message: result.data?.message || 'Invalid pincode',
            taxType: '',
            serviceable: false
          });
        }
      } catch (error) {
        setBillingPincodeStatus({
          loading: false,
          valid: false,
          message: 'Validation failed',
          taxType: '',
          serviceable: null
        });
      }
    };

    const timer = setTimeout(validatePincode, 500);
    return () => clearTimeout(timer);
  }, [billingAddress.pincode]);

  // Calculate totals
  const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
  const safeNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  const safePercent = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return 0;
    // Free input, but clamp to a sane range
    return Math.max(0, Math.min(100, n));
  };

  // Subtotal should always mean taxable/base amount (excl GST)
  const totals = items.reduce(
    (acc, item) => {
      const qty = safeNum(item.quantity);
      const unitPriceEntered = safeNum(item.unitPrice);
      const r = safePercent(item.gstRate) / 100;

      const unitPriceBase = rateIncludesGst ? (unitPriceEntered / (1 + r)) : unitPriceEntered;
      const baseLine = round2(qty * unitPriceBase);
      const grossLine = rateIncludesGst ? round2(qty * unitPriceEntered) : round2(baseLine * (1 + r));
      const gstLine = round2(grossLine - baseLine);

      acc.subtotal += baseLine;
      acc.total += grossLine;
      acc.gst += gstLine;
      return acc;
    },
    { subtotal: 0, total: 0, gst: 0 }
  );

  const subtotal = round2(totals.subtotal);
  const total = round2(totals.total);
  const gstAmount = round2(totals.gst);

  const isIntraState = billingPincodeStatus.taxType === 'CGST/SGST';
  const cgstAmount = isIntraState ? round2(gstAmount / 2) : 0;
  const sgstAmount = isIntraState ? round2(gstAmount - cgstAmount) : 0;
  const igstAmount = !isIntraState ? gstAmount : 0;

  const uniqueGstPercents = Array.from(new Set(items.map((i) => safePercent(i.gstRate))));
  const singleGstPercent = uniqueGstPercents.length === 1 ? uniqueGstPercents[0] : null;

  // Item handlers
  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, { name: '', description: '', hsn: '', quantity: 1, unitPrice: 0, gstRate: 0 }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  // Customer handlers
  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setUseCustomerAddress(true);
    setBillingAddress(prev => ({
      ...prev,
      companyName: customer.companyName || '',
      gst: customer.gst || ''
    }));
  };

  const handleCustomerCreate = (customer) => {
    setSelectedCustomer(customer);
    setShowCustomerModal(false);
    setUseCustomerAddress(true);
    setBillingAddress(prev => ({
      ...prev,
      companyName: customer.companyName || '',
      gst: customer.gst || ''
    }));
  };

  const handleCustomerClear = () => {
    setSelectedCustomer(null);
    setUseCustomerAddress(false);
    setBillingAddress({ address: '', city: '', state: '', pincode: '', companyName: '', billToCompany: false, gst: '' });
  };

  // Submit handler
  const handleSubmit = async () => {
    setError(null);
    
    // Validation
    if (!selectedCustomer) {
      setError('Please select or create a customer');
      return;
    }
    
    if (!billingAddress.pincode || billingAddress.pincode.length !== 6) {
      setError('Please enter a valid 6-digit billing pincode');
      return;
    }
    
    if (!billingPincodeStatus.valid) {
      setError('Please enter a valid billing pincode');
      return;
    }

    if (billingAddress.billToCompany && !billingAddress.companyName?.trim()) {
      setError('Please enter a company name when billing to company');
      return;
    }

    if (billingAddress.gst && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(billingAddress.gst.trim().toUpperCase())) {
      setError('Please enter a valid GST number');
      return;
    }
    
    const validItems = items.filter(item => item.name && parseFloat(item.unitPrice) > 0);
    if (validItems.length === 0) {
      setError('Please add at least one item with a name and price');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const token = sessionStorage.getItem('admin-token');
      const payload = {
        customer: { userId: selectedCustomer.userId },
        billingAddress,
        shippingAddress: sameAsShipping ? billingAddress : shippingAddress,
        items: validItems,
        rateIncludesGst,
        paymentMethod,
        transactionId: transactionId || null,
        notes: notes || null,
        invoiceDate
      };
      
      const response = await fetch(`${API_BASE_URL}/admin/invoices/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify(payload)
      });
      
      const result = await response.json();
      
      if (result.is_success) {
        setSuccess(result.data);
      } else {
        setError(result.error || 'Failed to create invoice');
      }
    } catch (error) {
      setError('Failed to create invoice. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Success view
  if (success) {
    return (
      <AdminLayout>
        <Card>
          <CardBody>
            <SuccessContainer>
              <SuccessIcon>✓</SuccessIcon>
              <SuccessTitle>Invoice Created Successfully!</SuccessTitle>
              <InvoiceNumber>{success.invoice.invoiceNumber}</InvoiceNumber>
              <SuccessText>
                Invoice has been created for {success.customer.firstname} {success.customer.lastname}
                <br />
                Total Amount: ₹{success.invoice.amount.toLocaleString('en-IN')}
              </SuccessText>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <SubmitButton onClick={() => navigate('/admin/orders')} style={{ width: 'auto' }}>
                  View Invoices
                </SubmitButton>
                <SecondaryButton 
                  onClick={() => {
                    setSuccess(null);
                    setSelectedCustomer(null);
                    setItems([{ name: '', description: '', hsn: '', quantity: 1, unitPrice: 0, gstRate: 0 }]);
                    setRateIncludesGst(false);
                    setBillingAddress({ address: '', city: '', state: '', pincode: '', companyName: '', billToCompany: false, gst: '' });
                    setPaymentMethod('cash');
                    setTransactionId('');
                    setNotes('');
                  }} 
                  style={{ width: 'auto', marginTop: 0 }}
                >
                  Create Another Invoice
                </SecondaryButton>
              </div>
            </SuccessContainer>
          </CardBody>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <PageHeader>
        <PageTitle>Create Invoice</PageTitle>
        <BackButton onClick={() => navigate('/admin/orders')}>
          ← Back to Orders
        </BackButton>
      </PageHeader>

      <FormContainer>
        <MainColumn>
          {/* Customer Section */}
          <Card style={{ overflow: 'visible', zIndex: 50 }}>
            <CardHeader>
              <CardTitle>Customer Details</CardTitle>
            </CardHeader>
            <CardBody className="customer-section" style={{ overflow: 'visible' }}>
              <CustomerPicker
                selectedCustomer={selectedCustomer}
                onSelect={handleCustomerSelect}
                onCreateNew={() => setShowCustomerModal(true)}
                onClear={handleCustomerClear}
              />
              
              {!selectedCustomer && (
                <div style={{ marginTop: '12px', textAlign: 'center' }}>
                  <SecondaryButton 
                    onClick={() => setShowCustomerModal(true)}
                    style={{ width: 'auto', display: 'inline-block', marginTop: 0 }}
                  >
                    + Create New Customer
                  </SecondaryButton>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Billing Address Section */}
          <Card>
            <CardHeader>
              <CardTitle>Billing Address</CardTitle>
            </CardHeader>
            <CardBody>
              {selectedCustomer && (
                <Checkbox style={{ marginBottom: '16px' }}>
                  <input
                    type="checkbox"
                    checked={useCustomerAddress}
                    onChange={(e) => {
                      setUseCustomerAddress(e.target.checked);
                      if (e.target.checked && selectedCustomer) {
                        setBillingAddress(prev => ({
                          address: selectedCustomer.address || '',
                          city: '',
                          state: '',
                          pincode: selectedCustomer.pincode?.toString() || '',
                          companyName: selectedCustomer.companyName || prev.companyName || '',
                          billToCompany: prev.billToCompany
                        }));
                      }
                    }}
                  />
                  Use customer's address
                </Checkbox>
              )}
              
              <FormGrid>
                <FormGroup className="full-width" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '4px' }}>
                  <Checkbox>
                    <input
                      type="checkbox"
                      checked={billingAddress.billToCompany}
                      onChange={(e) => setBillingAddress(prev => ({ ...prev, billToCompany: e.target.checked }))}
                    />
                    Bill to Company Name
                  </Checkbox>
                </FormGroup>
                {billingAddress.billToCompany && (
                  <>
                    <FormGroup>
                      <Label>Company Name</Label>
                      <Input
                        type="text"
                        value={billingAddress.companyName}
                        onChange={(e) => setBillingAddress(prev => ({ ...prev, companyName: e.target.value }))}
                        placeholder="Company / Business name"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>GST Number (Optional)</Label>
                      <Input
                        type="text"
                        value={billingAddress.gst}
                        onChange={(e) => setBillingAddress(prev => ({ ...prev, gst: e.target.value.toUpperCase() }))}
                        placeholder="e.g., 30AAAAA0000A1Z5"
                      />
                    </FormGroup>
                  </>
                )}
                <FormGroup className="full-width">
                  <Label>Address</Label>
                  <Textarea
                    value={billingAddress.address}
                    onChange={(e) => setBillingAddress(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="Street address, building, floor..."
                    disabled={useCustomerAddress && selectedCustomer?.address}
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Pincode <span>*</span></Label>
                  <Input
                    type="text"
                    value={billingAddress.pincode}
                    onChange={(e) => setBillingAddress(prev => ({ 
                      ...prev, 
                      pincode: e.target.value.replace(/[^0-9]/g, '').slice(0, 6)
                    }))}
                    placeholder="6-digit pincode"
                    maxLength={6}
                  />
                  {billingPincodeStatus.message && (
                    <PincodeStatus className={billingPincodeStatus.loading ? 'loading' : billingPincodeStatus.valid ? 'valid' : 'invalid'}>
                      {billingPincodeStatus.loading ? '⏳' : billingPincodeStatus.valid ? '✓' : '✗'} {billingPincodeStatus.message}
                      {billingPincodeStatus.valid && billingPincodeStatus.serviceable === true && (
                        <TaxBadge className="intra" style={{ marginLeft: '8px' }}>Serviceable</TaxBadge>
                      )}
                      {billingPincodeStatus.valid && billingPincodeStatus.serviceable === false && (
                        <TaxBadge className="inter" style={{ marginLeft: '8px', background: '#fff3cd', color: '#856404', border: '1px solid #ffeaa7' }}>Not Serviceable</TaxBadge>
                      )}
                      {billingPincodeStatus.taxType && (
                        <TaxBadge className={billingPincodeStatus.taxType === 'CGST/SGST' ? 'intra' : 'inter'}>
                          {billingPincodeStatus.taxType}
                        </TaxBadge>
                      )}
                    </PincodeStatus>
                  )}
                </FormGroup>
                
                <FormGroup>
                  <Label>City</Label>
                  <Input
                    type="text"
                    value={billingAddress.city}
                    onChange={(e) => setBillingAddress(prev => ({ ...prev, city: e.target.value }))}
                    placeholder="City"
                  />
                </FormGroup>
                
                <FormGroup className="full-width">
                  <Label>State</Label>
                  <Select
                    value={billingAddress.state}
                    onChange={(e) => setBillingAddress(prev => ({ ...prev, state: e.target.value }))}
                  >
                    <option value="">Select State</option>
                    {INDIAN_STATES.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </Select>
                </FormGroup>
              </FormGrid>
              
              <Checkbox style={{ marginTop: '16px' }}>
                <input
                  type="checkbox"
                  checked={sameAsShipping}
                  onChange={(e) => setSameAsShipping(e.target.checked)}
                />
                Shipping address same as billing
              </Checkbox>
              
              {!sameAsShipping && (
                <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e9ecef' }}>
                  <h4 style={{ margin: '0 0 16px', fontSize: '14px', color: '#495057' }}>Shipping Address</h4>
                  <FormGrid>
                    <FormGroup className="full-width">
                      <Label>Address</Label>
                      <Textarea
                        value={shippingAddress.address}
                        onChange={(e) => setShippingAddress(prev => ({ ...prev, address: e.target.value }))}
                        placeholder="Street address, building, floor..."
                      />
                    </FormGroup>
                    
                    <FormGroup>
                      <Label>Pincode</Label>
                      <Input
                        type="text"
                        value={shippingAddress.pincode}
                        onChange={(e) => setShippingAddress(prev => ({ 
                          ...prev, 
                          pincode: e.target.value.replace(/[^0-9]/g, '').slice(0, 6)
                        }))}
                        placeholder="6-digit pincode"
                        maxLength={6}
                      />
                    </FormGroup>
                    
                    <FormGroup>
                      <Label>City</Label>
                      <Input
                        type="text"
                        value={shippingAddress.city}
                        onChange={(e) => setShippingAddress(prev => ({ ...prev, city: e.target.value }))}
                        placeholder="City"
                      />
                    </FormGroup>
                    
                    <FormGroup className="full-width">
                      <Label>State</Label>
                      <Select
                        value={shippingAddress.state}
                        onChange={(e) => setShippingAddress(prev => ({ ...prev, state: e.target.value }))}
                      >
                        <option value="">Select State</option>
                        {INDIAN_STATES.map(state => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </Select>
                    </FormGroup>
                  </FormGrid>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Items Section */}
          <Card>
            <CardHeader>
              <CardTitle>Invoice Items</CardTitle>
            </CardHeader>
            <CardBody>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '12px' }}>
                <FormGroup style={{ maxWidth: 260, marginBottom: 0 }}>
                  <Label>Item rate is</Label>
                  <Select value={rateIncludesGst ? 'inclusive' : 'exclusive'} onChange={(e) => setRateIncludesGst(e.target.value === 'inclusive')}>
                    <option value="exclusive">Exclusive of GST</option>
                    <option value="inclusive">Inclusive of GST</option>
                  </Select>
                </FormGroup>
                <div style={{ fontSize: 12, color: '#6c757d', marginBottom: 2 }}>
                  Invoice will show <strong>taxable value</strong> + GST separately.
                </div>
              </div>
              <ItemsTable>
                <thead>
                  <tr>
                    <th style={{ width: '33%' }}>Item Name & Description</th>
                    <th style={{ width: '12%' }}>HSN/SAC</th>
                    <th style={{ width: '8%' }}>Qty</th>
                    <th style={{ width: '16%' }}>Unit Price {rateIncludesGst ? '(Incl. GST)' : '(Excl. GST)'}</th>
                    <th style={{ width: '9%' }}>GST %</th>
                    <th style={{ width: '14%' }}>Amount</th>
                    <th style={{ width: '10%' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={index}>
                      <td>
                        <ItemInput
                          type="text"
                          value={item.name}
                          onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                          placeholder="Item name"
                          style={{ marginBottom: '4px' }}
                        />
                        <ItemTextarea
                          value={item.description || ''}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          placeholder="Description (optional)"
                          rows={1}
                          style={{ fontSize: '11px', minHeight: '28px', maxHeight: '60px' }}
                        />
                      </td>
                      <td>
                        <ItemInput
                          type="text"
                          value={item.hsn}
                          onChange={(e) => handleItemChange(index, 'hsn', e.target.value)}
                          placeholder="HSN/SAC"
                        />
                      </td>
                      <td>
                        <ItemInput
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                        />
                      </td>
                      <td>
                        <ItemInput
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unitPrice}
                          onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                          placeholder="0.00"
                        />
                      </td>
                      <td>
                        <ItemInput
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.gstRate}
                          onChange={(e) => handleItemChange(index, 'gstRate', e.target.value)}
                          placeholder="0"
                        />
                      </td>
                      <td style={{ fontWeight: '500' }}>
                        {(() => {
                          const qty = safeNum(item.quantity);
                          const unitPriceEntered = safeNum(item.unitPrice);
                          const r = safePercent(item.gstRate) / 100;
                          const unitPriceBase = rateIncludesGst ? (unitPriceEntered / (1 + r)) : unitPriceEntered;
                          const baseAmount = round2(qty * unitPriceBase);
                          const grossAmount = rateIncludesGst ? round2(qty * unitPriceEntered) : round2(baseAmount * (1 + r));
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <div>₹{baseAmount.toLocaleString('en-IN')}</div>
                              {rateIncludesGst && baseAmount > 0 && (
                                <div style={{ fontSize: 11, color: '#7f8c8d' }}>
                                  Incl. GST: ₹{grossAmount.toLocaleString('en-IN')}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td>
                        <RemoveButton onClick={() => removeItem(index)} disabled={items.length === 1}>
                          ×
                        </RemoveButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </ItemsTable>
              
              <AddItemButton onClick={addItem}>
                + Add Item
              </AddItemButton>
            </CardBody>
          </Card>

          {/* Payment Section */}
          <Card>
            <CardHeader>
              <CardTitle>Payment Details</CardTitle>
            </CardHeader>
            <CardBody>
              <FormGrid>
                <FormGroup>
                  <Label>Payment Method</Label>
                  <Select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  >
                    {PAYMENT_METHODS.map(method => (
                      <option key={method.value} value={method.value}>{method.label}</option>
                    ))}
                  </Select>
                </FormGroup>
                
                <FormGroup>
                  <Label>Transaction ID (Optional)</Label>
                  <Input
                    type="text"
                    value={transactionId}
                    onChange={(e) => setTransactionId(e.target.value)}
                    placeholder="e.g., UPI ref, cheque number"
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Invoice Date & Time</Label>
                  <Input
                    type="datetime-local"
                    value={invoiceDate}
                    onChange={(e) => setInvoiceDate(e.target.value)}
                  />
                </FormGroup>
                
                <FormGroup className="full-width">
                  <Label>Notes (Optional)</Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any additional notes for the invoice..."
                    style={{ minHeight: '60px' }}
                  />
                </FormGroup>
              </FormGrid>
            </CardBody>
          </Card>
        </MainColumn>

        <SideColumn>
          {/* Summary Card */}
          <Card style={{ position: 'sticky', top: '20px' }}>
            <CardHeader>
              <CardTitle>Invoice Summary</CardTitle>
            </CardHeader>
            <CardBody>
              <SummaryRow>
                <span>Subtotal</span>
                <span>₹{subtotal.toLocaleString('en-IN')}</span>
              </SummaryRow>
              
              {billingPincodeStatus.valid && (
                <>
                  {isIntraState ? (
                    <>
                      <SummaryRow>
                        <span>CGST{singleGstPercent != null ? ` (${(singleGstPercent / 2).toFixed(2).replace(/\.00$/, '')}%)` : ''}</span>
                        <span>₹{cgstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </SummaryRow>
                      <SummaryRow>
                        <span>SGST{singleGstPercent != null ? ` (${(singleGstPercent / 2).toFixed(2).replace(/\.00$/, '')}%)` : ''}</span>
                        <span>₹{sgstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </SummaryRow>
                    </>
                  ) : (
                    <SummaryRow>
                      <span>IGST{singleGstPercent != null ? ` (${singleGstPercent.toFixed(2).replace(/\.00$/, '')}%)` : ''}</span>
                      <span>₹{igstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </SummaryRow>
                  )}
                </>
              )}
              
              {!billingPincodeStatus.valid && billingAddress.pincode.length === 6 && (
                <SummaryRow style={{ color: '#e74c3c' }}>
                  <span>GST</span>
                  <span>Enter valid pincode</span>
                </SummaryRow>
              )}
              
              <SummaryRow className="total">
                <span>Total</span>
                <span>₹{total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </SummaryRow>
              
              {billingPincodeStatus.valid && (
                <TaxInfo>
                  {isIntraState ? (
                    <>
                      <strong>Intra-State Supply</strong> (Goa)
                      <br />
                      Tax split: CGST + SGST{singleGstPercent != null ? ` (${(singleGstPercent / 2).toFixed(2).replace(/\.00$/, '')}% each)` : ''}
                    </>
                  ) : (
                    <>
                      <strong>Inter-State Supply</strong>
                      <br />
                      Tax: IGST{singleGstPercent != null ? ` (${singleGstPercent.toFixed(2).replace(/\.00$/, '')}%)` : ''}
                    </>
                  )}
                </TaxInfo>
              )}
              
              {error && <ErrorText>{error}</ErrorText>}
              
              <SubmitButton 
                onClick={handleSubmit}
                disabled={submitting || !selectedCustomer || !billingPincodeStatus.valid || subtotal === 0}
                style={{ marginTop: '20px' }}
              >
                {submitting ? 'Creating Invoice...' : 'Create Invoice'}
              </SubmitButton>
              
              <SecondaryButton onClick={() => navigate('/admin/orders')}>
                Cancel
              </SecondaryButton>
            </CardBody>
          </Card>
        </SideColumn>
      </FormContainer>

      {/* Customer Modal */}
      <CustomerModal
        isOpen={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
        onSave={handleCustomerCreate}
      />
    </AdminLayout>
  );
}

export default InvoiceCreate;
